import { AlertTriangle, SearchIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";

import type { RiderSaleFormValues } from "./rider-sale-schema";
import type { Product } from "@artisancode/api-types";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

interface RiderSaleLinesTableProps {
  form: UseFormReturn<RiderSaleFormValues>;
  fields: FieldArrayWithId<RiderSaleFormValues, "lines", "id">[];
  products: Product[];
  stockByProductId?: Map<string, number>;
  visibleIndexes: number[];
  productQuery: string;
  setProductQuery: (query: string) => void;
  totalJual: number;
  totalKembali?: number;
  totalSelisih?: number;
  totalSetoran: number;
}

export function RiderSaleLinesTable({
  form,
  fields,
  products,
  stockByProductId,
  visibleIndexes,
  productQuery,
  setProductQuery,
  totalJual,
  totalKembali = 0,
  totalSelisih = 0,
  totalSetoran,
}: RiderSaleLinesTableProps) {
  return (
    <>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={productQuery}
          onChange={(e) => setProductQuery(e.target.value)}
          placeholder="Cari produk..."
          className="pl-8"
        />
      </div>

      <div className="max-h-60 overflow-y-auto rounded-md border md:max-h-72 md:flex-1">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted text-xs">
            <tr>
              <th className="px-2 py-1.5 text-left">Produk</th>
              <th
                className="w-16 px-1.5 py-1.5 text-right sm:w-18 sm:px-2"
                title="Stok fisik tersedia di toko"
              >
                Stok Toko
              </th>
              <th
                className="w-16 px-1.5 py-1.5 text-right sm:w-20 sm:px-2"
                title="Jumlah produk yang dibawa rider"
              >
                Ambil
              </th>
              <th
                className="w-16 px-1.5 py-1.5 text-right sm:w-20 sm:px-2"
                title="Jumlah produk yang laku terjual"
              >
                Jual
              </th>
              <th
                className="w-16 px-1.5 py-1.5 text-right sm:w-20 sm:px-2"
                title="Jumlah produk yang dikembalikan ke toko"
              >
                Kembali
              </th>
              <th
                className="w-16 px-1.5 py-1.5 text-right sm:w-18 sm:px-2"
                title="Selisih hilang/rusak (Ambil - Jual - Kembali)"
              >
                Selisih
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleIndexes.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-2 py-4 text-center text-muted-foreground"
                >
                  Produk tidak ditemukan.
                </td>
              </tr>
            )}
            {visibleIndexes.map((index) => {
              const field = fields[index];
              if (!field) return null;
              const product = products[index];
              const onHand = product
                ? stockByProductId?.get(product.id)
                : undefined;
              const disp = form.watch(`lines.${index}.dispatchedQty`) || 0;
              const sold = form.watch(`lines.${index}.soldQty`) || 0;
              const ret = form.watch(`lines.${index}.returnedQty`) || 0;
              const diff = disp - sold - ret;
              const isInsufficientStoreStock =
                onHand !== undefined && disp > onHand;

              return (
                <tr key={field.id} className="border-t">
                  <td className="px-2 py-1">
                    <div className="flex flex-col">
                      <span className="font-medium text-xs sm:text-sm">
                        {product?.name}
                      </span>
                      {onHand !== undefined && onHand <= 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                          Stok toko habis
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span
                      className={`font-mono text-xs ${
                        isInsufficientStoreStock
                          ? "font-bold text-destructive"
                          : "text-muted-foreground"
                      }`}
                      title={
                        isInsufficientStoreStock
                          ? `Stok toko (${onHand}) tidak cukup untuk ambil ${disp}`
                          : undefined
                      }
                    >
                      {onHand !== undefined
                        ? onHand.toLocaleString("id-ID")
                        : "-"}
                    </span>
                  </td>
                  <td className="px-2 py-1">
                    <FormField
                      control={form.control}
                      name={`lines.${index}.dispatchedQty`}
                      render={({ field: f }) => (
                        <NumberInput
                          className="h-8 text-right"
                          value={f.value ?? 0}
                          onChange={f.onChange}
                        />
                      )}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <FormField
                      control={form.control}
                      name={`lines.${index}.soldQty`}
                      render={({ field: f, fieldState }) => (
                        <NumberInput
                          className="h-8 text-right"
                          value={f.value ?? 0}
                          onChange={f.onChange}
                          aria-invalid={!!fieldState.error}
                          title={fieldState.error?.message}
                        />
                      )}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <FormField
                      control={form.control}
                      name={`lines.${index}.returnedQty`}
                      render={({ field: f }) => (
                        <NumberInput
                          className="h-8 text-right"
                          value={f.value ?? 0}
                          onChange={f.onChange}
                        />
                      )}
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    {diff > 0 ? (
                      <Badge
                        variant="destructive"
                        className="h-6 px-1.5 font-mono text-[11px]"
                        title={`Selisih barang hilang/rusak: ${diff}`}
                      >
                        {diff}
                      </Badge>
                    ) : diff < 0 ? (
                      <span className="font-mono text-xs font-bold text-destructive">
                        {diff}
                      </span>
                    ) : (
                      <span className="font-mono text-xs text-muted-foreground">
                        0
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(
        (form.formState.errors.lines ?? []) as (
          | { soldQty?: unknown }
          | undefined
        )[]
      ).some((l) => l?.soldQty) && (
        <p className="text-sm text-destructive">
          Ada produk dengan Total Jual + Kembali melebihi Ambil.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm text-muted-foreground">
        <div className="space-y-0.5">
          <div>
            <span>Total terjual: </span>
            <span className="font-medium text-foreground">
              {totalJual}
            </span>{" "}
            unit
            {totalKembali > 0 && (
              <>
                <span className="mx-1">·</span>
                <span>Kembali: </span>
                <span className="font-medium text-foreground">
                  {totalKembali}
                </span>{" "}
                unit
              </>
            )}
          </div>
          {totalSelisih > 0 && (
            <p className="font-medium text-destructive">
              Selisih hilang / rusak: {totalSelisih} unit
            </p>
          )}
        </div>
        <p>
          Total yang harus disetorkan:{" "}
          <span className="font-medium text-foreground">
            Rp {totalSetoran.toLocaleString("id-ID")}
          </span>
        </p>
      </div>
    </>
  );
}
